/** Browser-local saved PO letterhead / GST defaults (per workstation). */

import { normalizeLetterheadLogoUrl } from "./constructionPoDocument";

export const PO_FORMAT_STORAGE_KEY = "indiramma.poFormat.v1";

export type PoFormatGstRowSaved = {
  label: string;
  ratePercent: number;
};

export type PoFormatSettings = {
  version: 1;
  /** Main heading on the PO (e.g. PURCHASE ORDER). */
  documentTitle: string;
  /** Short line under the accent bar (e.g. department or programme name). */
  letterheadTagline: string;
  /** Public path or absolute URL to left / primary logo (e.g. /branding/Telangana-LOGO.png). */
  logoPrimaryUrl: string;
  /** Optional right-side logo URL. */
  logoSecondaryUrl: string;
  /** Max height of primary logo in the PDF (px). */
  logoPrimaryHeightPx: number;
  logoSecondaryHeightPx: number;
  /** Header accent stripe and title underline (#RRGGBB). */
  accentColorHex: string;
  /** Optional footer line under the totals table. */
  footerNote: string;
  buyerName: string;
  buyerAddress: string;
  buyerGstin: string;
  /** Vendor / supplier name on printed PO when set; otherwise the PO’s subcontractor firm name is used. */
  defaultVendorName: string;
  /** Prefilled vendor address on download. */
  defaultVendorAddress: string;
  defaultVendorGstin: string;
  paymentTerms: string;
  /** Default ship-to / site block text when opening the download panel. */
  defaultShipTo: string;
  aggregateQtyDefault: boolean;
  gstRows: PoFormatGstRowSaved[];
};

function clampLogoHeight(n: number): number {
  if (!Number.isFinite(n)) return 56;
  return Math.min(160, Math.max(28, Math.round(n)));
}

function sanitizeAccentHex(hex: string): string {
  const t = hex.trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(t)) return t;
  return "#16365f";
}

export function defaultPoFormatSettings(): PoFormatSettings {
  return {
    version: 1,
    documentTitle: "PURCHASE ORDER",
    letterheadTagline: "",
    logoPrimaryUrl: "",
    logoSecondaryUrl: "",
    logoPrimaryHeightPx: 56,
    logoSecondaryHeightPx: 68,
    accentColorHex: "#16365f",
    footerNote: "",
    buyerName: "",
    buyerAddress: "",
    buyerGstin: "",
    defaultVendorName: "",
    defaultVendorAddress: "",
    defaultVendorGstin: "",
    paymentTerms: "As per agreement. Retention and statutory deductions as applicable.",
    defaultShipTo: "",
    aggregateQtyDefault: true,
    gstRows: [
      { label: "CGST", ratePercent: 9 },
      { label: "SGST", ratePercent: 9 },
    ],
  };
}

function clampGstRows(rows: unknown): PoFormatGstRowSaved[] {
  const fallback = defaultPoFormatSettings().gstRows;
  if (!Array.isArray(rows)) return fallback;
  const out: PoFormatGstRowSaved[] = [];
  for (const r of rows) {
    if (!r || typeof r !== "object") continue;
    const o = r as { label?: unknown; ratePercent?: unknown };
    const label = typeof o.label === "string" ? o.label.trim().slice(0, 64) : "Tax";
    const rp = Number(o.ratePercent);
    out.push({
      label: label || "Tax",
      ratePercent: Number.isFinite(rp) ? Math.min(100, Math.max(0, rp)) : 0,
    });
  }
  return out.length ? out : fallback;
}

export function normalizePoFormatSettings(raw: unknown): PoFormatSettings {
  const d = defaultPoFormatSettings();
  if (!raw || typeof raw !== "object") return d;
  const o = raw as Record<string, unknown>;
  const doc =
    typeof o.documentTitle === "string" && o.documentTitle.trim()
      ? o.documentTitle.trim().slice(0, 120)
      : d.documentTitle;
  const foot =
    typeof o.footerNote === "string" ? o.footerNote.slice(0, 2000) : d.footerNote;
  const tag =
    typeof o.letterheadTagline === "string" ? o.letterheadTagline.slice(0, 240) : d.letterheadTagline;
  const lp =
    typeof o.logoPrimaryUrl === "string" ? o.logoPrimaryUrl.trim().slice(0, 5000000) : d.logoPrimaryUrl;
  const ls =
    typeof o.logoSecondaryUrl === "string" ? o.logoSecondaryUrl.trim().slice(0, 5000000) : d.logoSecondaryUrl;
  const accent =
    typeof o.accentColorHex === "string" ? sanitizeAccentHex(o.accentColorHex) : d.accentColorHex;
  const ph = Number(o.logoPrimaryHeightPx);
  const sh = Number(o.logoSecondaryHeightPx);
  return {
    version: 1,
    documentTitle: doc,
    letterheadTagline: tag,
    logoPrimaryUrl: normalizeLetterheadLogoUrl(lp),
    logoSecondaryUrl: normalizeLetterheadLogoUrl(ls),
    logoPrimaryHeightPx: Number.isFinite(ph) ? clampLogoHeight(ph) : d.logoPrimaryHeightPx,
    logoSecondaryHeightPx: Number.isFinite(sh) ? clampLogoHeight(sh) : d.logoSecondaryHeightPx,
    accentColorHex: accent,
    footerNote: foot,
    buyerName: typeof o.buyerName === "string" ? o.buyerName.slice(0, 2000) : "",
    buyerAddress: typeof o.buyerAddress === "string" ? o.buyerAddress.slice(0, 4000) : "",
    buyerGstin: typeof o.buyerGstin === "string" ? o.buyerGstin.toUpperCase().slice(0, 32) : "",
    defaultVendorName:
      typeof o.defaultVendorName === "string" ? o.defaultVendorName.trim().slice(0, 500) : "",
    defaultVendorAddress:
      typeof o.defaultVendorAddress === "string" ? o.defaultVendorAddress.slice(0, 4000) : "",
    defaultVendorGstin:
      typeof o.defaultVendorGstin === "string" ? o.defaultVendorGstin.toUpperCase().slice(0, 32) : "",
    paymentTerms:
      typeof o.paymentTerms === "string" && o.paymentTerms.trim()
        ? o.paymentTerms.slice(0, 4000)
        : d.paymentTerms,
    defaultShipTo: typeof o.defaultShipTo === "string" ? o.defaultShipTo.slice(0, 4000) : "",
    aggregateQtyDefault: o.aggregateQtyDefault === false ? false : true,
    gstRows: clampGstRows(o.gstRows),
  };
}

export function loadPoFormatSettings(): PoFormatSettings {
  try {
    const raw = localStorage.getItem(PO_FORMAT_STORAGE_KEY);
    if (!raw) return defaultPoFormatSettings();
    return normalizePoFormatSettings(JSON.parse(raw) as unknown);
  } catch {
    return defaultPoFormatSettings();
  }
}

export function savePoFormatSettings(s: PoFormatSettings): void {
  const normalized = normalizePoFormatSettings(s);
  localStorage.setItem(PO_FORMAT_STORAGE_KEY, JSON.stringify(normalized));
}

/** UI GST rows with stable ids for React keys. */
export function savedGstToEditorRows(rows: PoFormatGstRowSaved[]): Array<{ id: string; label: string; rateInput: string }> {
  return rows.map((r, i) => ({
    id: `gst-saved-${i}-${r.label.replace(/\W+/g, "").slice(0, 12)}`,
    label: r.label,
    rateInput: String(r.ratePercent),
  }));
}

export function gstEditorRowsToSaved(rows: Array<{ label: string; rateInput: string }>): PoFormatGstRowSaved[] {
  return rows.map((r) => {
    const n = Number.parseFloat(String(r.rateInput).replace(/,/g, "").trim());
    return {
      label: (r.label || "Tax").trim().slice(0, 64) || "Tax",
      ratePercent: Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : 0,
    };
  });
}
